"use client";

import { useState } from 'react';
import { ButtonMessage } from '@/types/messages';
import { 
  Copy, 
  Phone, 
  ExternalLink, 
  Image as ImageIcon, 
  Video,
  FileText,
  CornerDownLeft,
  PhoneCall
} from 'lucide-react';

interface ButtonMessagePreviewProps {
  message: ButtonMessage;
}

const buttonTypeConfig = {
  quick_reply: { icon: CornerDownLeft, label: 'Quick Reply', color: 'bg-blue-100 text-blue-700' },
  cta_copy: { icon: Copy, label: 'Copy', color: 'bg-gray-100 text-gray-700' },
  cta_call: { icon: Phone, label: 'Call', color: 'bg-green-100 text-green-700' },
  cta_url: { icon: ExternalLink, label: 'URL', color: 'bg-purple-100 text-purple-700' },
  voice_call: { icon: PhoneCall, label: 'Voice Call', color: 'bg-orange-100 text-orange-700' },
};

// Separate components for media to avoid DOM manipulation issues
function MediaImage({ url }: { url: string }) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center p-6">
          <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-2">Failed to load image</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            This preview may fail due to CORS restrictions, but the media will work properly in WhatsApp. 
            Try opening the URL in a new tab to verify it works.
          </p>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-xs text-blue-500 hover:underline mt-1 inline-block"
          >
            Test URL →
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <img 
        src={url} 
        alt="Message media" 
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </>
  );
}

function MediaVideo({ url }: { url: string }) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center p-6">
          <Video className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-2">Failed to load video</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            This preview may fail due to CORS restrictions, but the media will work properly in WhatsApp. 
            Try opening the URL in a new tab to verify it works.
          </p>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-xs text-blue-500 hover:underline mt-1 inline-block"
          >
            Test URL →
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <video 
        src={url} 
        className="w-full h-full object-cover"
        controls
        onError={() => setHasError(true)}
      />
    </>
  );
}

function MediaDocument({ url, filename }: { url: string; filename?: string }) {
  const [hasError, ] = useState(false);

  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center p-6">
          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-2">Failed to load document</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            This preview may fail due to CORS restrictions, but the document will work properly in WhatsApp. 
            Try opening the URL in a new tab to verify it works.
          </p>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-xs text-blue-500 hover:underline mt-1 inline-block"
          >
            Test URL →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-50">
      <div className="text-center p-6">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600 mb-2">
          {filename || "Document Preview"}
        </p>
        <p className="text-xs text-gray-500 mb-3">
          Document preview not available in this format
        </p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-xs text-blue-500 hover:underline inline-block"
        >
          Open Document →
        </a>
      </div>
    </div>
  );
}

export function ButtonMessagePreview({ message }: ButtonMessagePreviewProps) {
  const { title, subTitle, media, buttons } = message;

  return (
    <div className="relative w-full h-full">
      {/* WhatsApp-style background */}
      <div 
        className="absolute inset-0 rounded-lg overflow-hidden"
        style={{
          backgroundColor: '#f5f1eb'
        }}
      >
        {/* Background pattern with opacity */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'url("https://storage.googleapis.com/stark-media/wa-bg-2.png")',
            backgroundRepeat: 'repeat',
            backgroundSize: 'cover'
          }}
        ></div>
      </div>

      {/* Content container */}
      <div className="relative z-10 p-4 h-full min-h-[400px]">
        {/* Message bubble */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[280px] ml-auto relative">
          {/* Message tail */}
          <div className="absolute -right-2 top-3 w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>

        {/* Media Preview */}
        {media && media.url && (
          <div className="relative">
            <div className="aspect-video bg-gray-200 relative overflow-hidden">
              {media.type === 'image' ? (
                <MediaImage url={media.url} />
              ) : media.type === 'video' ? (
                <MediaVideo url={media.url} />
              ) : (
                <MediaDocument url={media.url} filename={media.filename} />
              )}
            </div>
          </div>
        )}

            {/* Message Content */}
            <div className="p-4 pb-2">
              <div className="text-gray-900 text-base font-medium whitespace-pre-wrap">
                {title}
              </div>

              {subTitle && (
                <div className="text-gray-500 text-sm mt-1 whitespace-pre-wrap">{subTitle}</div>
              )}
              
              {/* Timestamp */}
              <div className="text-right mt-1">
                <span className="text-xs text-gray-400">
                  {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
            </div>

        {/* Buttons */}
        {buttons && buttons.length > 0 && (
          <div className="border-t">
            {buttons.map((button, index) => {
              const config = buttonTypeConfig[button.type];
              const Icon = config.icon;
              
              return (
                <div key={index} className="border-b last:border-b-0">
                  <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                    <Icon className="w-5 h-5 mr-3 text-green-600" />
                    <span className="text-green-600 font-medium text-sm">{button.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}